import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CreditCard, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientSubscriptionsPage = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    axios.get(`${API}/subscriptions/my`).then(res => {
      setSubscriptions(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const active = subscriptions.filter(s => s.status === 'active');
  const inactive = subscriptions.filter(s => s.status !== 'active');
  const current = activeTab === 'active' ? active : inactive;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="client-subscriptions-page">
      <button onClick={() => navigate('/client/services')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-services">
        <ArrowLeft size={16} /> Servicios
      </button>

      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Suscripciones
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Gestiona tus planes y membresías</p>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-100 rounded-2xl p-1" data-testid="subscription-tabs">
        <button
          onClick={() => setActiveTab('active')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all duration-300 ${
            activeTab === 'active'
              ? 'bg-white text-emerald-600 shadow-sm'
              : 'text-gray-400 hover:text-gray-600'
          }`}
          data-testid="tab-active-subs"
        >
          <CheckCircle2 size={14} />
          Activas ({active.length})
        </button>
        <button
          onClick={() => setActiveTab('inactive')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all duration-300 ${
            activeTab === 'inactive'
              ? 'bg-white text-gray-600 shadow-sm'
              : 'text-gray-400 hover:text-gray-600'
          }`}
          data-testid="tab-inactive-subs"
        >
          <XCircle size={14} />
          Inactivas ({inactive.length})
        </button>
      </div>

      {/* List */}
      {current.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-subscriptions">
          <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
            activeTab === 'active' ? 'bg-gradient-to-br from-emerald-50 to-teal-50' : 'bg-gradient-to-br from-gray-50 to-slate-50'
          }`}>
            {activeTab === 'active'
              ? <CheckCircle2 size={32} className="text-emerald-300" />
              : <XCircle size={32} className="text-gray-300" />
            }
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {activeTab === 'active' ? 'Sin suscripciones activas' : 'Sin suscripciones inactivas'}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {activeTab === 'active'
              ? 'Aun no tienes planes o membresías activos'
              : 'No tienes suscripciones canceladas o vencidas'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {current.map((sub) => (
            <div
              key={sub.id}
              className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm"
              data-testid={`subscription-${sub.id}`}
            >
              <div className={`h-1 ${sub.status === 'active' ? 'bg-gradient-to-r from-emerald-400 to-teal-500' : 'bg-gray-200'}`} />
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    sub.status === 'active' ? 'bg-emerald-50' : 'bg-gray-50'
                  }`}>
                    <CreditCard size={20} className={sub.status === 'active' ? 'text-emerald-500' : 'text-gray-400'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-gray-900 text-sm">{sub.name}</p>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                        sub.status === 'active'
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {sub.status === 'active' ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    {sub.description && (
                      <p className="text-xs text-gray-400 mt-1">{sub.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      {sub.price > 0 && (
                        <p className="text-sm font-bold text-gray-900">${sub.price.toFixed(2)}<span className="text-[10px] text-gray-400 font-normal">/{sub.period || 'mes'}</span></p>
                      )}
                      {sub.expires_at && (
                        <p className="text-[11px] text-gray-400">
                          {sub.status === 'active' ? 'Vence:' : 'Vencio:'} {new Date(sub.expires_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientSubscriptionsPage;
